/**
 * Login Page
 * ===========
 * User authentication page.
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, LogIn } from 'lucide-react';
import toast from 'react-hot-toast';

import { useLogin } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { apiClient } from '@/api/client';

// =============================================================================
// VALIDATION
// =============================================================================

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

// =============================================================================
// COMPONENT
// =============================================================================

export default function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const [dentuluLoading, setDentuluLoading] = useState(false);
  const { mutate: login, isPending } = useLogin();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  // Check for SSO errors in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    const message = params.get('message');
    
    if (error === 'user_not_found') {
      toast.error(message || 'No account found with this email. Please register first.');
      // Clean URL
      window.history.replaceState({}, '', '/login');
    } else if (error === 'account_inactive') {
      toast.error(message || 'Your account is inactive. Please contact support.');
      window.history.replaceState({}, '', '/login');
    } else if (error) {
      toast.error(message || 'Authentication failed. Please try again.');
      window.history.replaceState({}, '', '/login');
    }
  }, []);

  // Load Dentulu SDK
  useEffect(() => {
    const sdkUrl = import.meta.env.VITE_DENTULU_SDK_URL || 'https://app.dentulu.com/js/dentulu-sdk.js';
    const script = document.createElement('script');
    script.src = sdkUrl;
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup: remove script when component unmounts
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const onSubmit = (data: LoginFormData) => {
    login(data);
  };

  const handleDentuluSSO = async () => {
    try {
      setDentuluLoading(true);
      
      // Get redirect URI for callback (backend URL)
      const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8001';
      const redirectUri = `${apiBaseUrl}/api/v1/auth/dentulu-callback`;
      const sandbox = import.meta.env.VITE_DENTULU_SANDBOX === '1';
      
      // Call backend to initiate SSO and get auth URL
      const response = await apiClient.post('/auth/dentulu/initiate', {
        sandbox,
        redirect_uri: redirectUri,
      });
      
      const { auth_url, state } = response.data;
      
      // Store state in sessionStorage for validation in callback
      sessionStorage.setItem('dentulu_oauth_state', state);
      
      // Open auth URL in popup window (like Google OAuth)
      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;
      
      const popup = window.open(
        auth_url,
        'DentuluProAuth',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`
      );
      
      if (!popup) {
        toast.error('Please allow popups for this website');
        setDentuluLoading(false);
        return;
      }
      
      // Listen for message from Dentulu SDK (when authentication completes)
      // const messageHandler = async (event: MessageEvent) => {
      //   // Handle messages from Dentulu SDK
      //   if (event.data && event.data.source === 'dentulu-pro') {
      //     console.log('📩 Received Dentulu SDK response:', event.data);
      //     console.log('📩 event.data.success:', event.data.success);
      //     console.log('📩 event.data.user:', event.data.user);
      //     console.log('📩 event.data type:', typeof event.data.user);
          
      //     if (event.data.success && event.data.user) {
      //       try {
      //         // The Dentulu SDK sends the backend response in event.data.user
      //         const response = event.data.user;
      //         console.log('🔍 Full response object:', response);
      //         console.log('🔍 Response type:', typeof response);
      //         console.log('🔍 Response keys:', response ? Object.keys(response) : 'response is null/undefined');
              
      //         const { access_token, refresh_token, user } = response;
              
      //         console.log('🔍 Extracted data:', {
      //           hasAccessToken: !!access_token,
      //           hasRefreshToken: !!refresh_token,
      //           hasUser: !!user,
      //           user: user
      //         });
              
      //         if (access_token && refresh_token && user) {
      //           // Login successful - store tokens
      //           const { useAuthStore } = await import('@/stores/authStore');
      //           useAuthStore.getState().setAuth(access_token, refresh_token, user);
                
      //           popup.close();
      //           window.removeEventListener('message', messageHandler);
      //           setDentuluLoading(false);
                
      //           // Show success and redirect to dashboard
      //           toast.success(`Welcome, ${user.first_name}!`);
      //           navigate('/dashboard', { replace: true });
      //         } else {
      //           console.error('❌ Missing data - access_token:', !!access_token, 'refresh_token:', !!refresh_token, 'user:', !!user);
      //           throw new Error('Missing authentication data');
      //         }
      //       } catch (error: any) {
      //         console.error('❌ Error processing Dentulu response:', error);
      //         console.error('❌ Error stack:', error.stack);
      //         popup.close();
      //         window.removeEventListener('message', messageHandler);
      //         setDentuluLoading(false);
      //         toast.error('Failed to process authentication. Please try again.');
      //       }
      //     } else if (event.data.error) {
      //       // Handle error from backend
      //       popup.close();
      //       window.removeEventListener('message', messageHandler);
      //       setDentuluLoading(false);
            
      //       const errorDetail = event.data.error.detail || 'Authentication failed';
            
      //       // Show user-friendly messages
      //       if (errorDetail.includes('No account found') || errorDetail.includes('not found')) {
      //         toast.error('Account not found. Please register first or contact support.');
      //       } else if (errorDetail.includes('inactive')) {
      //         toast.error('Your account is inactive. Please contact support.');
      //       } else {
      //         toast.error(errorDetail);
      //       }
      //     }
      //     return;
      //   }
        
      //   // Legacy handling for old callback page approach
      //   if (event.origin !== window.location.origin) {
      //     return;
      //   }
        
      //   if (event.data.type === 'dentulu-auth-success') {
      //     popup.close();
      //     window.removeEventListener('message', messageHandler);
      //     setDentuluLoading(false);
      //   } else if (event.data.type === 'dentulu-auth-error') {
      //     popup.close();
      //     window.removeEventListener('message', messageHandler);
      //     setDentuluLoading(false);
      //     toast.error(event.data.message || 'Authentication failed');
      //   }
      // };
      
      // window.addEventListener('message', messageHandler);
      
      // Check if popup was closed manually
      const popupCheck = setInterval(() => {
        if (popup.closed) {
          clearInterval(popupCheck);
          // window.removeEventListener('message', messageHandler);
          // setDentuluLoading(false);
        }
      }, 500);
      
    } catch (error: any) {
      console.error('Dentulu SSO error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to initiate Dentulu Pro authentication';
      toast.error(errorMessage);
      setDentuluLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900">
            Periodontal Charting
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Sign in to your account
          </p>
        </div>

        {/* Form */}
        <Card className="p-8">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <Input
              label="Email address"
              type="email"
              autoComplete="email"
              {...register('email')}
              error={errors.email?.message}
            />

            <div className="relative">
              <Input
                label="Password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                {...register('password')}
                error={errors.password?.message}
              />
              <button
                type="button"
                className="absolute right-3 top-9 text-gray-400 hover:text-gray-600"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                  Remember me
                </label>
              </div>

              <Link
                to="/forgot-password"
                className="text-sm font-medium text-primary-600 hover:text-primary-500"
              >
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
              className="w-full"
              isLoading={isPending}
            >
              <LogIn className="w-4 h-4 mr-2" />
              Sign in
            </Button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  Or continue with
                </span>
              </div>
            </div>

            <div className="mt-6">
              <button
                type="button"
                onClick={handleDentuluSSO}
                disabled={dentuluLoading}
                className="w-full inline-flex items-center justify-center gap-2 border border-blue-500 bg-white text-blue-600 font-semibold text-sm py-2.5 px-4 rounded-lg hover:bg-blue-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {dentuluLoading ? (
                  <>
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Connecting...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path fillRule="evenodd" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" clipRule="evenodd" />
                    </svg>
                    <span>Continue with Dentulu Pro</span>
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  Don't have an account?
                </span>
              </div>
            </div>

            <div className="mt-6">
              <Link to="/register">
                <Button variant="outline" className="w-full">
                  Create an account
                </Button>
              </Link>
            </div>
          </div>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-gray-500">
          By signing in, you agree to our{' '}
          <a href="#" className="text-primary-600 hover:text-primary-500">
            Terms of Service
          </a>{' '}
          and{' '}
          <a href="#" className="text-primary-600 hover:text-primary-500">
            Privacy Policy
          </a>
        </p>
      </div>
    </div>
  );
}
